import { useEffect, useRef, useState } from "react";
import { Instagram, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInstagramFeed } from "@/hooks/useInstagramFeed";

const MAX_POSTS = 6;

const FALLBACK_ALTS = [
  "Armação italiana de óculos de grau para óptica, Atacado Óptico",
  "Lentes oftálmicas e acessórios para óptica, Atacado Óptico",
  "Óculos de grau modernos para revenda, Atacado Óptico",
  "Produtos ópticos exclusivos marca própria, Atacado Óptico",
  "Óculos solar polarizado com proteção UV, Atacado Óptico",
  "Armação titânio fino para óptica premium, Atacado Óptico",
];

const INITIAL_FALLBACK_COUNT = 3;

interface BeholdInstagramFeedProps {
  /** Behold feed ID. Defaults to the main @atacado_optico feed. */
  feedId?: string;
  /** Instagram handle for the fallback CTA. */
  instagramHandle?: string;
  /** Number of columns per row on desktop. */
  desktopColumns?: 3 | 4 | 5;
  /** Static images shown as mini-grid when the live feed fails. */
  fallbackImages?: string[];
  /** Called when user clicks "Abrir Instagram" in the error fallback. */
  onAbrirClick?: () => void;
  /** Called when user clicks "Ver mais no Instagram" in the fallback mini-grid. */
  onVerMaisClick?: () => void;
}

export function BeholdInstagramFeed({
  feedId = "7ESTTWDjt4ePAPRS6cZD",
  instagramHandle = "atacado_optico",
  desktopColumns = 4,
  fallbackImages = [],
  onAbrirClick,
  onVerMaisClick,
}: BeholdInstagramFeedProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleFallback, setVisibleFallback] = useState(INITIAL_FALLBACK_COUNT);
  const [announcement, setAnnouncement] = useState("");

  // Lazy-load: dispara o fetch quando a seção se aproxima da viewport.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px 600px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { posts, loading, error } = useInstagramFeed(feedId, MAX_POSTS, isVisible);

  useEffect(() => {
    if (loading && isVisible) setAnnouncement("Carregando feed do Instagram...");
    else if (error) setAnnouncement("Feed do Instagram indisponível. Exibindo posts recentes salvos.");
    else if (posts.length > 0) setAnnouncement(`${posts.length} posts do Instagram carregados.`);
    else setAnnouncement("");
  }, [loading, error, posts.length, isVisible]);

  const desktopColsClass =
    desktopColumns === 3
      ? "md:grid-cols-3"
      : desktopColumns === 5
        ? "md:grid-cols-5"
        : "md:grid-cols-4";

  const handleLoadMore = () => {
    setVisibleFallback(fallbackImages.length);
    setAnnouncement(`Todas as ${fallbackImages.length} fotos carregadas.`);
    onVerMaisClick?.();
  };

  const hasFallback = fallbackImages.length > 0;
  const visibleImages = fallbackImages.slice(0, visibleFallback);
  const hasMore = visibleFallback < fallbackImages.length;

  return (
    <div className="w-full">
      <div ref={sentinelRef} aria-hidden="true" />
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {error ? (
        hasFallback ? (
          <div>
            <p role="alert" className="sr-only">
              Feed do Instagram indisponível. Exibindo posts recentes salvos.
            </p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {visibleImages.map((src, i) => (
                <a
                  key={i}
                  href={`https://instagram.com/${instagramHandle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square overflow-hidden rounded-xl group cursor-pointer block"
                  aria-label={FALLBACK_ALTS[i] || `Post ${i + 1} do Instagram do Atacado Óptico`}
                >
                  <img
                    src={src}
                    alt={FALLBACK_ALTS[i] || `Post ${i + 1} do Instagram do Atacado Óptico`}
                    loading="lazy"
                    decoding="async"
                    width={320}
                    height={320}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </a>
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={handleLoadMore}
                  className="flex items-center gap-2 text-sm font-semibold text-accent border border-accent/30 hover:border-accent px-6 py-3 min-h-11 rounded-full transition-colors hover:bg-accent/5"
                >
                  <Instagram className="h-4 w-4" aria-hidden="true" />
                  Ver mais no Instagram
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-10 px-4 text-center min-h-[420px] md:min-h-[520px]">
            <AlertCircle className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground max-w-sm">
              Não foi possível carregar o feed do Instagram agora. Veja nossas
              publicações diretamente no perfil.
            </p>
            <Button
              asChild
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={onAbrirClick}
            >
              <a
                href={`https://instagram.com/${instagramHandle}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="h-4 w-4" aria-hidden="true" />
                Abrir Instagram
              </a>
            </Button>
          </div>
        )
      ) : (
        <div className={`grid grid-cols-2 ${desktopColsClass} gap-2 md:gap-3`}>
          {loading
            ? Array.from({ length: desktopColumns * 2 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-md bg-muted/40 animate-pulse"
                  aria-hidden="true"
                />
              ))
            : posts.map((post) => (
                <a
                  key={post.id}
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative aspect-square overflow-hidden rounded-md group block bg-muted/40"
                  aria-label={post.caption || `Ver publicação no Instagram @${instagramHandle}`}
                  onClick={onAbrirClick}
                >
                  <img
                    src={post.imageUrl}
                    alt={post.caption || "Publicação do Instagram do Atacado Óptico"}
                    loading="lazy"
                    decoding="async"
                    width={320}
                    height={320}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {post.isVideo && (
                    <span
                      className="absolute top-2 right-2 text-white drop-shadow"
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  )}
                </a>
              ))}
        </div>
      )}
    </div>
  );
}
